import { createStore, type StoreApi } from 'zustand';

import type { LanguageCodeForJSA } from '~/shared/internationalization';

import type {
    ProfileTaskDraftReference,
    ProfileTaskParentContext,
    ProfileTaskSummary,
    ProfileTaskVersionInput,
} from '../model/profile-task';
import {
    addTaskEditor,
    adoptCompletedRemovals,
    adoptSavedBaseline,
    appendPersistedTaskReference,
    applyTaskEditor,
    canReplaceProfileTaskEditor,
    createParentDraftState,
    discardParentDraft,
    discardTaskEditor,
    getProfileTaskParentDraftKey,
    type OpenProfileTaskEditorInput,
    openTaskEditor,
    type ProfileTaskParentDraftState,
    type ProfileTaskSavePreparation,
    prepareParentSave,
    removeTaskFromParent,
    reorderAppliedTasks,
    updateTaskEditor,
} from './profile-task-parent-state';

export type {
    OpenProfileTaskEditorInput,
    ProfileTaskEditorState,
    ProfileTaskParentDraftState,
    ProfileTaskSavePreparation,
} from './profile-task-parent-state';
export { getProfileTaskParentDraftKey, hasProfileTaskDraftChanges } from './profile-task-parent-state';

export type ProfileTaskDraftCoordinatorStore = {
    readonly parentLanguages: ReadonlyMap<string, LanguageCodeForJSA>;
    readonly parents: ReadonlyMap<string, ProfileTaskParentDraftState>;
    /** Titles of freshly generated tasks, bridging the gap until a read that contains them lands. */
    readonly pendingTaskTitles: ReadonlyMap<string, string>;
    readonly addTask: (input: OpenProfileTaskEditorInput) => void;
    readonly appendPersistedTask: (
        context: ProfileTaskParentContext,
        task: { readonly taskId: string; readonly taskVersionId: string; readonly title: string },
    ) => void;
    readonly applyEditor: (context: ProfileTaskParentContext, taskId: string) => boolean;
    readonly canReplaceEditor: (context: ProfileTaskParentContext, taskId: string) => boolean;
    readonly clearParent: (context: ProfileTaskParentContext) => void;
    readonly discardEditor: (context: ProfileTaskParentContext, taskId: string) => void;
    readonly discardParent: (context: ProfileTaskParentContext) => void;
    readonly getParent: (context: ProfileTaskParentContext) => ProfileTaskParentDraftState | undefined;
    readonly initialize: (context: ProfileTaskParentContext, baseline: readonly ProfileTaskDraftReference[]) => void;
    readonly openEditor: (input: OpenProfileTaskEditorInput) => void;
    readonly prepareSave: (context: ProfileTaskParentContext) => ProfileTaskSavePreparation;
    readonly removeTask: (context: ProfileTaskParentContext, taskId: string) => void;
    readonly reorder: (context: ProfileTaskParentContext, fromIndex: number, toIndex: number) => void;
    readonly saveFailed: (context: ProfileTaskParentContext, completedRemovalIds?: readonly string[]) => void;
    readonly saveSucceeded: (
        context: ProfileTaskParentContext,
        parentVersion: number,
        summaries: readonly ProfileTaskSummary[],
    ) => void;
    readonly setParentLanguage: (context: ProfileTaskParentContext, language: LanguageCodeForJSA) => void;
    readonly updateEditor: (context: ProfileTaskParentContext, taskId: string, form: ProfileTaskVersionInput) => void;
};

export type ProfileTaskDraftCoordinator = StoreApi<ProfileTaskDraftCoordinatorStore>;

const updateParent = (
    parents: ReadonlyMap<string, ProfileTaskParentDraftState>,
    context: ProfileTaskParentContext,
    update: (parent: ProfileTaskParentDraftState) => ProfileTaskParentDraftState,
): ReadonlyMap<string, ProfileTaskParentDraftState> => {
    const key = getProfileTaskParentDraftKey(context);
    const parent = parents.get(key);
    if (parent === undefined) {
        return parents;
    }

    const next = new Map(parents);
    next.set(key, update(parent));
    return next;
};

export const createProfileTaskDraftCoordinator = (): ProfileTaskDraftCoordinator =>
    createStore<ProfileTaskDraftCoordinatorStore>((set, get) => ({
        parentLanguages: new Map(),
        parents: new Map(),
        pendingTaskTitles: new Map(),
        addTask: (input) => {
            set(({ parents }) => ({
                parents: updateParent(parents, input.context, (parent) => addTaskEditor(parent, input)),
            }));
        },
        appendPersistedTask: (context, task) => {
            set(({ parents, pendingTaskTitles }) => ({
                parents: updateParent(parents, context, (parent) => appendPersistedTaskReference(parent, task)),
                pendingTaskTitles: new Map(pendingTaskTitles ?? []).set(task.taskId, task.title),
            }));
        },
        applyEditor: (context, taskId) => {
            const parent = get().getParent(context);
            const next = parent === undefined ? undefined : applyTaskEditor(parent, taskId);
            if (next === undefined) {
                return false;
            }
            set(({ parents }) => ({ parents: updateParent(parents, context, () => next) }));
            return true;
        },
        canReplaceEditor: (context, taskId) => canReplaceProfileTaskEditor(get().getParent(context), taskId),
        clearParent: (context) => {
            const key = getProfileTaskParentDraftKey(context);
            set(({ parents }) => {
                if (!parents.has(key)) {
                    return { parents };
                }
                const next = new Map(parents);
                next.delete(key);
                return { parents: next };
            });
        },
        discardEditor: (context, taskId) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, (parent) => discardTaskEditor(parent, taskId)),
            }));
        },
        discardParent: (context) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, discardParentDraft),
            }));
        },
        getParent: (context) => get().parents.get(getProfileTaskParentDraftKey(context)),
        initialize: (context, baseline) => {
            const key = getProfileTaskParentDraftKey(context);
            set(({ parents }) => {
                if (parents.has(key)) {
                    return { parents };
                }
                return { parents: new Map(parents).set(key, createParentDraftState(context, baseline)) };
            });
        },
        openEditor: (input) => {
            set(({ parents }) => ({
                parents: updateParent(parents, input.context, (parent) => openTaskEditor(parent, input)),
            }));
        },
        prepareSave: (context) => {
            const parent = get().getParent(context);
            if (parent === undefined) {
                return { ok: true, ordered: [], removedTaskIds: [] };
            }
            return prepareParentSave(parent);
        },
        removeTask: (context, taskId) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, (parent) => removeTaskFromParent(parent, taskId)),
            }));
        },
        reorder: (context, fromIndex, toIndex) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, (parent) => reorderAppliedTasks(parent, fromIndex, toIndex)),
            }));
        },
        saveFailed: (context, completedRemovalIds = []) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, (parent) =>
                    adoptCompletedRemovals(parent, completedRemovalIds),
                ),
            }));
        },
        saveSucceeded: (context, parentVersion, summaries) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, (parent) =>
                    adoptSavedBaseline(parent, context, parentVersion, summaries),
                ),
            }));
        },
        setParentLanguage: (context, language) => {
            const key = getProfileTaskParentDraftKey(context);
            set(({ parentLanguages }) => {
                if (parentLanguages.get(key) === language) {
                    return { parentLanguages };
                }
                return { parentLanguages: new Map(parentLanguages).set(key, language) };
            });
        },
        updateEditor: (context, taskId, form) => {
            set(({ parents }) => ({
                parents: updateParent(parents, context, (parent) => updateTaskEditor(parent, taskId, form)),
            }));
        },
    }));
